import React, { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { Box } from "@mui/material";
import { useToastStack, ToastStack } from "../../../../shared/components/ActionToast";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { FullscreenOutlined, FullscreenExitOutlined, ViewListOutlined, CloseOutlined } from "@mui/icons-material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { EventContentArg, DatesSetArg, EventClickArg, EventDropArg } from "@fullcalendar/core";
import { EventReceiveArg, EventResizeDoneArg } from "@fullcalendar/interaction";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import {
  fetchScheduleWorkOrders, fetchWorkOrders,
  updateWorkOrder, changeWorkOrderStatus,
  regenerateFutureInstances,
} from "../../store/workOrdersSlice";
import { dt } from "../../../../shared/themes/redesignTokens";
import { WorkOrder, WorkOrderStatusCode } from "../../models/models";
import { RootState } from "../../../../core/store";
import { calendarCssOverrides } from "../schedule/calendarStyles";
import ScheduleHeader from "../schedule/ScheduleHeader";
import ScheduleEventContent from "../schedule/ScheduleEventContent";
import ScheduleLegend from "../schedule/ScheduleLegend";
import CalendarPillRail, { ActivePanel } from "./CalendarPillRail";
import CalendarBacklogPanel from "./CalendarBacklogPanel";
import CalendarRecurringPanel from "./CalendarRecurringPanel";
import RecurringEditDialog, { RecurringEditChoice } from "./RecurringEditDialog";
import { buildUpdateModel, mapWorkOrdersToEvents, toLocalDateTimeString } from "./calendarUtils";
import Loader from "../../../../shared/components/Loader";
import dayjs from "dayjs";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

interface BoardCalendarViewProps {
  canWrite: boolean;
  canManage: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

interface UndoReschedule {
  workOrderId: string;
  workOrderNumber: string;
  oldStart: string | undefined;
  oldEnd: string | undefined;
  oldDuration: number | undefined;
  newDate: string;
}

const BOARD_FETCH_PARAMS = { pageNumber: 1, pageSize: 500, statusIds: [1, 2, 3, 4, 5, 6, 7] };

const BoardCalendarView: React.FC<BoardCalendarViewProps> = ({
  canWrite, canManage, onFullscreenChange,
}) => {
  const { translate } = useTranslation("operations");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const calendarRef = useRef<FullCalendar>(null);

  // ─── Responsive breakpoints (same as WorkBoardPage) ───
  const { isPhone, isTablet, isDesktop } = useBreakpoint();

  const { scheduleWorkOrders = [], scheduleLoading = false, workOrders = [] } =
    useAppSelector((state: RootState) => state.workOrders);

  // Phone forces day view only
  const [currentView, setCurrentView] = useState(() => isPhone ? "timeGridDay" : "timeGridWeek");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { addToast, dismissToast, toasts } = useToastStack();
  const undoToastIdRef = useRef<number | null>(null);
  const undoStateRef = useRef<UndoReschedule | null>(null);
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ─── Tablet: collapsible backlog drawer state ───
  const [tabletDrawerOpen, setTabletDrawerOpen] = useState(false);

  // ─── Phone: bottom sheet backlog state ───
  const [phoneBacklogOpen, setPhoneBacklogOpen] = useState(false);

  // ─── Recurring edit dialog state (Shift+drag) ───
  const shiftHeldRef = useRef(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const pendingRecurringDropRef = useRef<{
    wo: WorkOrder;
    newStart: string;
    newEnd: string | undefined;
    revert: () => void;
  } | null>(null);

  // Force day view on phone whenever breakpoint changes
  useEffect(() => {
    if (isPhone && currentView !== "timeGridDay") {
      setCurrentView("timeGridDay");
      calendarRef.current?.getApi().changeView("timeGridDay");
    }
  }, [isPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Track Shift key for recurring series drag ───
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === "Shift") shiftHeldRef.current = true; };
    const onKeyUp = (e: KeyboardEvent) => { if (e.key === "Shift") shiftHeldRef.current = false; };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => { window.removeEventListener("keydown", onKeyDown); window.removeEventListener("keyup", onKeyUp); };
  }, []);

  const handleTogglePanel = useCallback((panel: "backlog" | "recurring") => {
    setActivePanel((prev) => (prev === panel ? null : panel));
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => { const next = !prev; onFullscreenChange?.(next); return next; });
  }, [onFullscreenChange]);

  const loadData = useCallback(() => {
    if (!dateRange) return Promise.resolve();
    return dispatch(fetchScheduleWorkOrders({ dateFrom: dateRange.start, dateTo: dateRange.end }));
  }, [dispatch, dateRange]);

  useEffect(() => {
    if (dateRange) { loadData(); if (!initialLoadDone) setInitialLoadDone(true); }
  }, [dateRange, loadData]); // eslint-disable-line react-hooks/exhaustive-deps

  const backlogWOs = useMemo(
    () => workOrders
      .filter(
        (wo) => wo.workOrderStatusId === WorkOrderStatusCode.Created ||
                wo.workOrderStatusId === WorkOrderStatusCode.Assigned
      )
      .sort((a, b) => {
        const posA = a.boardPosition ?? Infinity;
        const posB = b.boardPosition ?? Infinity;
        if (posA !== posB) return posA - posB;
        return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
      }),
    [workOrders]
  );
  const backlogCount = backlogWOs.length;

  const handleDatesSet = useCallback((info: DatesSetArg) => {
    setCurrentView(info.view.type);
    setCurrentDate(info.view.currentStart);
    // FullCalendar timeGrid views emit ISO strings with timezone offset
    // (e.g. "2026-03-30T00:00:00+02:00") while dayGrid emits date-only
    // ("2026-03-30"). The timezone offset causes .NET to shift DateFrom/DateTo
    // when parsed, cutting off events near range boundaries. Normalise to
    // date-only "YYYY-MM-DD" strings so the backend receives consistent values.
    const toDateOnly = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    setDateRange({ start: toDateOnly(info.start), end: toDateOnly(info.end) });
  }, []);

  const calendarEvents = useMemo(() => mapWorkOrdersToEvents(scheduleWorkOrders), [scheduleWorkOrders]);

  const handleEventClick = useCallback(
    (info: EventClickArg) => { if (info.event.id) navigate(`/operations/work-orders/${info.event.id}`); },
    [navigate]
  );

  // Drag-to-reschedule existing calendar events
  //
  // CRITICAL: Do NOT call setExtendedProp(), loadData(), or any React setState
  // synchronously inside eventDrop. FullCalendar's interaction plugin fires
  // eventDrop from within its drag-end lifecycle (EventDragging.handleDragEnd).
  // Any call that triggers a FullCalendar internal MERGE_EVENTS dispatch
  // (setExtendedProp) or a React re-render (setState → componentDidUpdate →
  // resetOptions) during this lifecycle corrupts the interaction plugin's
  // internal state for the dropped event — making it un-draggable until a
  // DIFFERENT event is dragged (which resets the stale reference).
  //
  // Fix: Capture all needed data synchronously, then defer ALL side effects
  // (React state, API call) to the next event-loop tick via setTimeout(0).
  // This lets FullCalendar's cleanup() finish first, keeping the interaction
  // plugin's state clean.
  const handleEventDrop = useCallback(
    (info: EventDropArg) => {
      const wo: WorkOrder | undefined = info.event.extendedProps?.workOrder;
      if (!wo || !info.event.start) { info.revert(); return; }

      // Capture values synchronously — info.event references become stale
      // after FullCalendar's internal cleanup runs.
      const oldStart = wo.scheduledStartDate;
      const oldEnd = wo.scheduledEndDate;
      const newStart = toLocalDateTimeString(info.event.start);
      const newEnd = info.event.end ? toLocalDateTimeString(info.event.end) : undefined;
      const woId = wo.id;
      const woNumber = wo.number;
      const newDateLabel = formatUiDate(info.event.start, "dayMonthTime");

      // Shift+drag on a recurring WO → show confirmation dialog
      const isRecurring = !!wo.recurrenceParentId;
      const isShiftDrag = shiftHeldRef.current;

      if (isRecurring && isShiftDrag) {
        // Stash the pending drop so the dialog callback can apply it
        // Capture revert fn for use if user cancels
        const revertFn = info.revert;
        setTimeout(() => {
          pendingRecurringDropRef.current = { wo, newStart, newEnd, revert: revertFn };
          setRecurringDialogOpen(true);
        }, 0);
        return;
      }

      // Default: reschedule this occurrence only (no dialog)
      // Defer ALL side effects to after FullCalendar's drag lifecycle completes.
      setTimeout(() => {
        showCalendarUndoToast({
          workOrderId: woId, workOrderNumber: woNumber,
          oldStart, oldEnd, oldDuration: wo.estimatedDurationMinutes,
          newDate: newDateLabel,
        });

        dispatch(updateWorkOrder({
          id: woId,
          model: buildUpdateModel(wo, newStart, newEnd),
        })).unwrap().catch(() => {
          info.revert();
          clearCalendarUndoToast();
        });
      }, 0);
    },
    [dispatch]
  );

  // Resize bottom handle — change event duration
  //
  // CRITICAL: Same deferred-side-effects pattern as handleEventDrop.
  // Synchronous setState/dispatch inside eventResize corrupts FullCalendar's
  // interaction plugin state. Capture all data synchronously, defer side effects.
  const handleEventResize = useCallback(
    (info: EventResizeDoneArg) => {
      const wo: WorkOrder | undefined = info.event.extendedProps?.workOrder;
      if (!wo) { info.revert(); return; }

      const newStart = info.event.start;
      const newEnd = info.event.end;
      if (!newStart || !newEnd) { info.revert(); return; }

      // Capture values synchronously before FullCalendar's cleanup runs
      const newDurationMinutes = Math.round((newEnd.getTime() - newStart.getTime()) / 60000);
      const newStartStr = toLocalDateTimeString(newStart);
      const newEndStr = toLocalDateTimeString(newEnd);
      const oldStart = wo.scheduledStartDate;
      const oldEnd = wo.scheduledEndDate;
      const oldDuration = wo.estimatedDurationMinutes;
      const woId = wo.id;
      const woNumber = wo.number;
      const newDateLabel = `${dayjs(newStart).format("HH:mm")} — ${dayjs(newEnd).format("HH:mm")} (${newDurationMinutes}min)`;

      // Defer ALL side effects to after FullCalendar's resize lifecycle completes.
      setTimeout(() => {
        showCalendarUndoToast({
          workOrderId: woId, workOrderNumber: woNumber,
          oldStart, oldEnd, oldDuration,
          newDate: newDateLabel,
        });

        dispatch(updateWorkOrder({
          id: woId,
          model: buildUpdateModel(wo, newStartStr, newEndStr, { estimatedDurationMinutes: newDurationMinutes }),
        })).unwrap().catch(() => {
          info.revert();
          clearCalendarUndoToast();
        });
      }, 0);
    },
    [dispatch]
  );

  // External drag from backlog panel → calendar
  const handleEventReceive = useCallback(
    async (info: EventReceiveArg) => {
      const woId = info.draggedEl.getAttribute("data-wo-id");
      if (!woId || !info.event.start) { info.revert(); return; }
      const wo = workOrders.find((w) => w.id === woId);
      if (!wo) { info.revert(); return; }

      const newStart = toLocalDateTimeString(info.event.start);
      const newEnd = info.event.end ? toLocalDateTimeString(info.event.end) : undefined;

      try {
        await dispatch(updateWorkOrder({
          id: wo.id,
          model: buildUpdateModel(wo, newStart, newEnd),
        })).unwrap();
        if (wo.workOrderStatusId <= 2) {
          await dispatch(changeWorkOrderStatus({
            id: wo.id, model: { newStatusId: 3, createServiceReport: false },
          })).unwrap();
        }
        showCalendarUndoToast({
          workOrderId: wo.id, workOrderNumber: wo.number,
          oldStart: wo.scheduledStartDate, oldEnd: undefined,
          oldDuration: wo.estimatedDurationMinutes,
          newDate: formatUiDate(info.event.start, "dayMonthTime"),
        });
        // Remove the FC-created placeholder and refetch authoritative data
        info.event.remove();
        await loadData();
        dispatch(fetchWorkOrders(BOARD_FETCH_PARAMS));
      } catch { info.revert(); loadData(); }
    },
    [dispatch, workOrders, loadData]
  );

  // ─── Undo helpers (ref-based for toast action callbacks) ──────
  const handleUndoRef = useRef<() => void>(() => {});
  handleUndoRef.current = async () => {
    const state = undoStateRef.current;
    if (!state) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    const wo = scheduleWorkOrders.find((w) => w.id === state.workOrderId)
      || workOrders.find((w) => w.id === state.workOrderId);
    undoStateRef.current = null;
    if (undoToastIdRef.current != null) { dismissToast(undoToastIdRef.current); undoToastIdRef.current = null; }
    if (!wo) return;
    try {
      await dispatch(updateWorkOrder({
        id: wo.id,
        model: buildUpdateModel(wo, state.oldStart, state.oldEnd,
          state.oldDuration !== undefined ? { estimatedDurationMinutes: state.oldDuration } : undefined),
      })).unwrap();
      loadData(); dispatch(fetchWorkOrders(BOARD_FETCH_PARAMS));
    } catch { loadData(); }
  };

  const showCalendarUndoToast = useCallback((undoData: UndoReschedule) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoStateRef.current = undoData;
    if (undoToastIdRef.current != null) dismissToast(undoToastIdRef.current);
    undoToastIdRef.current = addToast("undo", translate("calendar.rescheduled", { number: undoData.workOrderNumber, date: undoData.newDate }), {
      duration: 5000,
      action: (
        <Box
          onClick={() => handleUndoRef.current()}
          sx={{
            fontFamily: dt.font,
            fontWeight: 700,
            fontSize: "12px",
            color: "#3B82F6",
            cursor: "pointer",
            textTransform: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {translate("calendar.undo")}
        </Box>
      ),
    });
    undoTimerRef.current = setTimeout(() => { undoStateRef.current = null; }, 5000);
  }, [addToast, dismissToast, translate]);

  const clearCalendarUndoToast = useCallback(() => {
    undoStateRef.current = null;
    if (undoToastIdRef.current != null) { dismissToast(undoToastIdRef.current); undoToastIdRef.current = null; }
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, [dismissToast]);

  const renderEventContent = useCallback(
    (eventInfo: EventContentArg) => <ScheduleEventContent eventInfo={eventInfo} />, []
  );

  // ─── Recurring dialog handlers ───────────────────────────────
  const handleRecurringDialogConfirm = useCallback(
    async (choice: RecurringEditChoice) => {
      setRecurringDialogOpen(false);
      const pending = pendingRecurringDropRef.current;
      if (!pending) return;
      pendingRecurringDropRef.current = null;
      const { wo, newStart, newEnd, revert } = pending;

      if (choice === "thisOnly") {
        // Same as default drag — update this occurrence only
        try {
          await dispatch(updateWorkOrder({
            id: wo.id,
            model: buildUpdateModel(wo, newStart, newEnd),
          })).unwrap();
          loadData();
        } catch {
          revert();
          loadData();
        }
      } else {
        // "allFuture": update the template, then regenerate future instances
        const templateId = wo.recurrenceParentId;
        if (!templateId) { revert(); return; }
        try {
          await dispatch(updateWorkOrder({
            id: templateId,
            model: buildUpdateModel(wo, newStart, newEnd),
          })).unwrap();
          await dispatch(regenerateFutureInstances(templateId)).unwrap();
          loadData();
        } catch {
          revert();
          loadData();
        }
      }
    },
    [dispatch, loadData]
  );

  const handleRecurringDialogCancel = useCallback(() => {
    setRecurringDialogOpen(false);
    const pending = pendingRecurringDropRef.current;
    if (pending) { pending.revert(); }
    pendingRecurringDropRef.current = null;
  }, []);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Schedule sub-header + fullscreen toggle */}
      <div className="flex items-center">
        <div className="flex-1 min-w-0">
          <ScheduleHeader calendarRef={calendarRef} currentView={currentView}
            currentDate={currentDate}
            onViewChange={(v) => { if (!isPhone) setCurrentView(v); }}
            hideViewToggle={isPhone}
            compact={isPhone} />
        </div>
        {/* Fullscreen toggle — hide on phone */}
        {!isPhone && (
          <button onClick={handleToggleFullscreen}
            className="mr-8 w-7 h-7 rounded-[6px] bg-white border border-mid-border flex items-center justify-center text-mid-muted hover:text-mid-charcoal hover:border-mid-charcoal transition-colors flex-shrink-0"
            title={isFullscreen ? translate("calendar.exitFullscreen") : translate("calendar.fullscreen")}>
            {isFullscreen ? <FullscreenExitOutlined sx={{ fontSize: 18 }} /> : <FullscreenOutlined sx={{ fontSize: 18 }} />}
          </button>
        )}
      </div>

      {/* Legend — hide on phone to save vertical space */}
      {!isPhone && <ScheduleLegend />}

      {scheduleLoading && !initialLoadDone ? (
        <div className="flex justify-center py-20"><Loader /></div>
      ) : (
        <div className={`flex flex-1 min-h-0 ${isPhone ? "mx-2 mb-2" : "mx-8 mb-5"} relative`}>

          {/* ─── Desktop: Pill rail + panels (unchanged) ─── */}
          {isDesktop && !isFullscreen && (
            <CalendarPillRail activePanel={activePanel} onToggle={handleTogglePanel} backlogCount={backlogCount} />
          )}
          {isDesktop && !isFullscreen && activePanel === "backlog" && (
            <CalendarBacklogPanel workOrders={workOrders}
              onCreateClick={() => window.dispatchEvent(new CustomEvent("openCreatePanel"))}
              canWrite={canWrite} />
          )}
          {isDesktop && !isFullscreen && activePanel === "recurring" && <CalendarRecurringPanel />}

          {/* ─── Tablet: Collapsible backlog drawer ─── */}
          {isTablet && !isFullscreen && (
            <>
              {/* Edge tab — always visible when drawer is closed */}
              {!tabletDrawerOpen && (
                <button
                  onClick={() => setTabletDrawerOpen(true)}
                  className="absolute left-0 top-12 z-20 flex items-center gap-1.5 pl-2 pr-2.5 py-2 rounded-r-full bg-white border border-l-0 border-mid-border shadow-sm hover:shadow-md transition-all"
                  style={{ fontFamily: "'Sora', sans-serif", writingMode: "horizontal-tb" }}
                >
                  <ViewListOutlined sx={{ fontSize: 14, color: "#8C9099" }} />
                  <span className="text-[11px] font-semibold text-mid-charcoal whitespace-nowrap">
                    {translate("board.backlog")}
                  </span>
                  <span
                    className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[9px] font-bold text-white px-1"
                    style={{ fontFamily: "'JetBrains Mono', monospace", backgroundColor: "#D4572B" }}
                  >
                    {backlogCount > 99 ? "99+" : backlogCount}
                  </span>
                </button>
              )}

              {/* Sliding drawer panel */}
              <div
                className={`absolute left-0 top-0 bottom-0 z-30 flex transition-transform duration-250 ease-out ${
                  tabletDrawerOpen ? "translate-x-0" : "-translate-x-full"
                }`}
              >
                <div className="w-[240px] bg-white border-r border-mid-border shadow-lg flex flex-col min-h-0 relative">
                  {/* Close button overlay in top-right corner */}
                  <button
                    onClick={() => setTabletDrawerOpen(false)}
                    className="absolute top-2.5 right-2 z-10 w-6 h-6 rounded flex items-center justify-center text-mid-muted hover:text-mid-charcoal hover:bg-mid-board transition-colors"
                  >
                    <CloseOutlined sx={{ fontSize: 16 }} />
                  </button>
                  {/* Reuse CalendarBacklogPanel — it has its own header */}
                  <CalendarBacklogPanel workOrders={workOrders}
                    onCreateClick={() => { setTabletDrawerOpen(false); window.dispatchEvent(new CustomEvent("openCreatePanel")); }}
                    canWrite={canWrite} />
                </div>
              </div>

              {/* Backdrop overlay when drawer is open */}
              {tabletDrawerOpen && (
                <div
                  className="absolute inset-0 z-20 bg-black/10"
                  onClick={() => setTabletDrawerOpen(false)}
                />
              )}
            </>
          )}

          {/* ─── Calendar container ─── */}
          <div className={`bg-white ${isPhone ? "rounded-lg" : "rounded-xl"} border border-[#E8E5DF] overflow-hidden flex-1 min-w-0 min-h-0 relative`}
            style={{ animation: "fadeSlideUp 0.4s ease both" }}>
            <style>{calendarCssOverrides}</style>
            <FullCalendar ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={isPhone ? "timeGridDay" : "timeGridWeek"}
              headerToolbar={false} events={calendarEvents}
              editable={canWrite} eventStartEditable={canWrite} eventDurationEditable={canWrite}
              droppable={!isPhone}
              eventDrop={handleEventDrop} eventResize={handleEventResize}
              eventReceive={handleEventReceive}
              eventClick={handleEventClick} datesSet={handleDatesSet}
              eventContent={renderEventContent}
              snapDuration="00:15:00"
              eventResizableFromStart={false}
              defaultTimedEventDuration="01:00:00"
              height="100%" weekNumbers={!isPhone} nowIndicator
              slotMinTime="06:00:00" slotMaxTime="22:00:00"
              allDaySlot={true} eventDisplay="block" firstDay={1}
              dayMaxEvents={isPhone ? 3 : 4} expandRows stickyHeaderDates
              weekNumberFormat={{ week: "numeric" }}
              longPressDelay={isPhone ? 500 : 1000} />
            {initialLoadDone && scheduleWorkOrders.length === 0 && !scheduleLoading && (
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
                <span className={`${isPhone ? "text-xs" : "text-sm"} text-[#8C9099]`} style={{ fontFamily: "'Sora', sans-serif" }}>
                  {translate("calendar.noEvents")}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Phone: Backlog bottom sheet ─── */}
      {isPhone && (
        <>
          {/* Floating backlog button (bottom-right) */}
          {!phoneBacklogOpen && (
            <button
              onClick={() => setPhoneBacklogOpen(true)}
              className="fixed bottom-[120px] right-4 z-40 flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-white border border-mid-border shadow-lg active:scale-95 transition-transform"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              <ViewListOutlined sx={{ fontSize: 16, color: "#8C9099" }} />
              <span className="text-xs font-semibold text-mid-charcoal">
                {translate("board.backlog")}
              </span>
              <span
                className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[9px] font-bold text-white px-1"
                style={{ fontFamily: "'JetBrains Mono', monospace", backgroundColor: "#D4572B" }}
              >
                {backlogCount > 99 ? "99+" : backlogCount}
              </span>
            </button>
          )}

          {/* Bottom sheet */}
          {phoneBacklogOpen && (
            <div className="fixed inset-0 z-50 flex flex-col justify-end">
              {/* Backdrop */}
              <div className="absolute inset-0 bg-black/20" onClick={() => setPhoneBacklogOpen(false)} />

              {/* Sheet content */}
              <div
                className="relative bg-white rounded-t-2xl border-t border-mid-border shadow-2xl flex flex-col max-h-[60vh] overflow-hidden"
                style={{ animation: "slideUpSheet 0.25s ease both" }}
              >
                {/* Handle bar */}
                <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
                  <div className="w-10 h-1 rounded-full bg-[#C1C7CD]" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-mid-border flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-mid-charcoal" style={{ fontFamily: "'Sora', sans-serif" }}>
                      {translate("board.backlog")}
                    </span>
                    <span
                      className="min-w-[20px] h-[20px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1.5"
                      style={{ fontFamily: "'JetBrains Mono', monospace", backgroundColor: "#D4572B" }}
                    >
                      {backlogCount}
                    </span>
                  </div>
                  <button
                    onClick={() => setPhoneBacklogOpen(false)}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-mid-muted hover:text-mid-charcoal hover:bg-mid-board transition-colors"
                  >
                    <CloseOutlined sx={{ fontSize: 18 }} />
                  </button>
                </div>

                {/* Backlog items — scrollable */}
                <div
                  className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-2"
                  style={{ scrollbarWidth: "thin", scrollbarColor: "#C1C7CD transparent" }}
                >
                  {backlogWOs.length === 0 ? (
                    <div className="text-center py-8">
                      <span className="text-xs text-mid-muted" style={{ fontFamily: "'Sora', sans-serif" }}>
                        {translate("calendar.noBacklog")}
                      </span>
                    </div>
                  ) : (
                    backlogWOs.map((wo) => (
                      <button
                        key={wo.id}
                        onClick={() => { setPhoneBacklogOpen(false); navigate(`/operations/work-orders/${wo.id}`); }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-white border border-mid-border text-left active:bg-mid-board transition-colors min-h-[48px]"
                      >
                        <div className="w-[3px] self-stretch rounded-full flex-shrink-0"
                          style={{ backgroundColor: wo.workOrderTypeId === 1 ? "#D4572B" : wo.workOrderTypeId === 2 ? "#3B82F6" : wo.workOrderTypeId === 3 ? "#8C6BD4" : wo.workOrderTypeId === 4 ? "#2D8C4E" : "#6B7280" }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-medium text-mid-muted" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                              {wo.number}
                            </span>
                            {wo.priorityCode <= 2 && (
                              <span className="text-[8px] font-semibold px-1.5 py-px rounded"
                                style={{
                                  backgroundColor: wo.priorityCode === 1 ? "rgba(212,87,43,0.1)" : "rgba(232,168,23,0.1)",
                                  color: wo.priorityCode === 1 ? "#D4572B" : "#C08E12",
                                }}>
                                {wo.priorityCode === 1 ? "CRIT" : "HIGH"}
                              </span>
                            )}
                          </div>
                          <span className="text-[13px] font-semibold text-mid-charcoal line-clamp-1 mt-0.5" style={{ fontFamily: "'Sora', sans-serif" }}>
                            {wo.title}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* + New button */}
                {canWrite && (
                  <div className="flex-shrink-0 px-3 pb-3 pt-1 border-t border-mid-border">
                    <button
                      onClick={() => { setPhoneBacklogOpen(false); window.dispatchEvent(new CustomEvent("openCreatePanel")); }}
                      className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold text-mid-muted bg-[#F7F5F0] active:bg-mid-border transition-colors min-h-[44px]"
                      style={{ fontFamily: "'Sora', sans-serif" }}
                    >
                      {translate("quickCreate.addWorkOrder")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bottom sheet animation */}
          <style>{`
            @keyframes slideUpSheet {
              from { transform: translateY(100%); }
              to   { transform: translateY(0); }
            }
          `}</style>
        </>
      )}

      {/* Recurring series edit dialog (shown on Shift+drag of recurring WO) */}
      <RecurringEditDialog
        open={recurringDialogOpen}
        onConfirm={handleRecurringDialogConfirm}
        onCancel={handleRecurringDialogCancel}
      />

      {/* Stacking toast notifications */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default BoardCalendarView;
